import { apiCall } from '../../services/apiUtil';

// What things cost. A price hangs off any entity — an event, a series, a
// programme, a tournament, a court, a membership — so every call takes the
// owner as a pair rather than assuming one kind of parent.
//
// Writes go through the academy because charging money is an academy
// capability (`manage_programs`); reads do not need one.
export const PaymentPricingApi = {
  list: async (academyId, ownerType, ownerId, { includeInactive = false, audience, at } = {}) => {
    const qs = new URLSearchParams({ ownerType, ownerId: String(ownerId) });
    if (includeInactive) qs.set('includeInactive', 'true');
    if (audience) qs.set('audience', audience);
    if (at)       qs.set('at', at);
    return apiCall(`/academies/${academyId}/prices?${qs.toString()}`);
  },

  create: async (academyId, data) => apiCall(`/academies/${academyId}/prices`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  update: async (academyId, priceId, data) => apiCall(`/academies/${academyId}/prices/${priceId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  }),

  // Withdraws rather than deletes: order lines point at the price they were
  // bought at, so the row has to outlive its use.
  deactivate: async (academyId, priceId) => apiCall(`/academies/${academyId}/prices/${priceId}`, {
    method: 'DELETE',
  }),

  // Names this academy already prices under, plus the currency it charges in.
  // The currency comes from here rather than being guessed client-side, so a
  // host abroad still edits in the venue's money.
  tags: async (academyId) => apiCall(`/academies/${academyId}/price-tags`),

  // What one occurrence actually costs, after the event → series → programme
  // walk. Reports which level answered, which is what lets the UI say whether a
  // price is the event's own or inherited.
  forEvent: async (eventId, { audience } = {}) => {
    const qs = audience ? `?audience=${encodeURIComponent(audience)}` : '';
    return apiCall(`/events/${eventId}/prices${qs}`);
  },
};

/**
 * Price CRUD, as the academy settings screens call it.
 *
 * Was a second `prices` block inside academyAPI — the same endpoints reached
 * two different ways depending on which screen you came from. One module now,
 * so a change to how prices are written cannot land in only half the app.
 */
export const PaymentPricesApi = {
    list: async (academyId, ownerType, ownerId, params = {}) => {
      const qs = new URLSearchParams({ ownerType, ownerId: String(ownerId), ...params }).toString();
      return apiCall(`/academies/${academyId}/prices?${qs}`);
    },
    create: async (academyId, payload) =>
      apiCall(`/academies/${academyId}/prices`, { method: 'POST', body: JSON.stringify(payload) }),
    update: async (academyId, priceId, payload) =>
      apiCall(`/academies/${academyId}/prices/${priceId}`, { method: 'PATCH', body: JSON.stringify(payload) }),
    withdraw: async (academyId, priceId) =>
      apiCall(`/academies/${academyId}/prices/${priceId}`, { method: 'DELETE' }),
};

// Kept as `pricingAPI` too so existing call sites read naturally; the exported
// name carries the Payment prefix so the module is greppable.
export const pricingAPI = PaymentPricingApi;
